import { createContext, useContext, useEffect, useState, useCallback } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { getActiveOrgId, setActiveOrgId, subscribeActiveOrg } from "./active-org";
import { getMyOrganizations, type Organization } from "./api/organizations";

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  orgs: Organization[];
  currentOrg: Organization | null;
  switchOrg: (orgId: string) => void;
  refreshOrgs: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  loading: true,
  orgs: [],
  currentOrg: null,
  switchOrg: () => {},
  refreshOrgs: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(getActiveOrgId());

  const refreshOrgs = useCallback(async () => {
    if (!session?.user) {
      if (!loading) {
        setOrgs([]);
        setActiveOrgId(null);
        setCurrentOrgIdState(null);
      }
      return;
    }
    try {
      const list = await getMyOrganizations();
      setOrgs(list);
      const persisted = getActiveOrgId();
      const valid = list.find((o) => o.id === persisted)?.id ?? list[0]?.id ?? null;
      setActiveOrgId(valid);
      setCurrentOrgIdState(valid);
    } catch {
      setOrgs([]);
    }
  }, [session?.user, loading]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (loading) return;
    void refreshOrgs();
  }, [refreshOrgs, loading]);

  useEffect(() => subscribeActiveOrg(setCurrentOrgIdState), []);

  const switchOrg = useCallback(
    (orgId: string) => {
      const found = orgs.find((o) => o.id === orgId);
      if (!found) return;
      setActiveOrgId(orgId);
      setCurrentOrgIdState(orgId);
    },
    [orgs],
  );

  const currentOrg = orgs.find((o) => o.id === currentOrgId) ?? null;

  return (
    <AuthContext.Provider
      value={{
        user: session?.user ?? null,
        session,
        loading,
        orgs,
        currentOrg,
        switchOrg,
        refreshOrgs,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
